import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  selectPreferences,
  setTheme,
  setPreferences,
  type ThemeMode,
} from '../../../store/features/preferences/preferencesSlice';
import { updateUserPreferences } from '../../../lib/apiClient';
import { formatDateTime } from '../../../lib/dateFormat';
import {
  THEME_OPTIONS,
  TIMEZONE_OPTIONS,
  LOCALE_OPTIONS,
  withValue,
} from '../../../lib/preferenceOptions';
import './index.css';

export const Route = createFileRoute('/settings/preferences/')({
  component: PreferencesPage,
});

export { PreferencesPage };

const ForgeButton = 'forge-button' as React.ElementType;

function PreferencesPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const prefs = useAppSelector(selectPreferences);

  // Theme applies immediately; locale/timezone are staged until Save.
  const [locale, setLocaleDraft] = useState<string>(prefs.locale);
  const [timezone, setTimezoneDraft] = useState<string>(prefs.timezone);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const localeOptions = withValue(LOCALE_OPTIONS, prefs.locale);
  const timezoneOptions = withValue(TIMEZONE_OPTIONS, prefs.timezone);

  const isDirty = locale !== prefs.locale || timezone !== prefs.timezone;

  const handleThemeChange = (value: string): void => {
    dispatch(setTheme(value as ThemeMode));
    setSaved(false);
    // Persist the theme immediately so the choice survives reload.
    void updateUserPreferences({ theme: value as ThemeMode }).catch(() => {
      // Non-fatal; the localStorage mirror keeps it across reloads.
    });
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await updateUserPreferences({ locale, timezone });
      dispatch(
        setPreferences({
          theme: updated.theme,
          locale: updated.locale,
          timezone: updated.timezone,
        })
      );
      setLocaleDraft(updated.locale);
      setTimezoneDraft(updated.timezone);
      setSaved(true);
    } catch {
      setError('Could not save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Live preview uses the staged locale/timezone so the user sees the effect
  // before saving.
  const previewNow = formatDateTime(new Date(), { locale, timezone });

  return (
    <div className="preferences-page">
      <h1 className="forge-typography--headline4">Preferences</h1>
      <p className="forge-typography--body1 preferences-intro">
        Personalize how the platform looks and how dates are displayed.
      </p>

      <section className="preferences-section" aria-labelledby="prefs-appearance">
        <h2 id="prefs-appearance" className="forge-typography--heading4">
          Appearance
        </h2>
        <div className="preferences-field">
          <label htmlFor="theme-select" className="form-label">
            Theme
          </label>
          <select
            id="theme-select"
            data-testid="theme-select"
            className="forge-select"
            value={prefs.theme}
            onChange={(e) => handleThemeChange(e.target.value)}
          >
            {THEME_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="form-helper-text">
            &ldquo;System&rdquo; follows your device&rsquo;s light/dark setting.
          </div>
        </div>
      </section>

      <section className="preferences-section" aria-labelledby="prefs-locale">
        <h2 id="prefs-locale" className="forge-typography--heading4">
          Language &amp; Region
        </h2>
        <div className="preferences-field">
          <label htmlFor="locale-select" className="form-label">
            Language (date &amp; number format)
          </label>
          <select
            id="locale-select"
            data-testid="locale-select"
            className="forge-select"
            value={locale}
            onChange={(e) => {
              setLocaleDraft(e.target.value);
              setSaved(false);
            }}
          >
            {localeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="form-helper-text">
            Detected from your browser; used to format dates and numbers.
          </div>
        </div>

        <div className="preferences-field">
          <label htmlFor="timezone-select" className="form-label">
            Timezone
          </label>
          <select
            id="timezone-select"
            data-testid="timezone-select"
            className="forge-select"
            value={timezone}
            onChange={(e) => {
              setTimezoneDraft(e.target.value);
              setSaved(false);
            }}
          >
            {timezoneOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="form-helper-text" data-testid="date-preview">
            Dates will display like: {previewNow}
          </div>
        </div>
      </section>

      <div className="preferences-actions">
        <ForgeButton
          type="button"
          variant="raised"
          disabled={!isDirty || saving}
          onClick={() => void handleSave()}
          data-testid="save-preferences"
        >
          {saving ? 'Saving…' : 'Save preferences'}
        </ForgeButton>
        {saved && (
          <span className="preferences-status preferences-status--success" role="status">
            <forge-icon name="check_circle" aria-hidden="true"></forge-icon>
            Saved
          </span>
        )}
        {error && (
          <span className="preferences-status preferences-status--error" role="alert">
            {error}
          </span>
        )}
      </div>
    </div>
  );
}
